import { useAuth, useGreeterVenueApi } from "@greeter/matter";
import { isType, mapQuery } from "@greeter/util";
import { useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useMemo } from "react";

export type FeatureFlag = "ticket-event.v1.venue" | "wardrobe.v1.venue";

export function useFeatureFlagQuery(
  featureFlag: FeatureFlag,
  options?: { enabled?: boolean }
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: ["featureFlag", featureFlag],
    async queryFn() {
      return await api.fetchVenueFeatureFlag({
        name: featureFlag as string,
      });
    },
    enabled: options?.enabled,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: 5_000,
  });
}

export type FeatureFlagsProviderValue = {
  "ticket-events.v1.venue": boolean;
  "wardrobe.v1.venue": boolean;
};

export const FeatureFlagsContext =
  React.createContext<FeatureFlagsProviderValue>({
    "ticket-events.v1.venue": false,
    "wardrobe.v1.venue": false,
  });

export function FeatureFlagsProvider(props: React.PropsWithChildren) {
  const auth = useAuth();
  const ticketEventsV1 = useFeatureFlagQuery("ticket-event.v1.venue", {
    enabled: auth.isLoggedIn,
  });
  const wardrobeV1 = useFeatureFlagQuery("wardrobe.v1.venue", {
    enabled: auth.isLoggedIn,
  });

  return (
    <FeatureFlagsContext.Provider
      {...props}
      value={{
        "ticket-events.v1.venue": ticketEventsV1.data?.active ?? false,
        "wardrobe.v1.venue": wardrobeV1.data?.active ?? false,
      }}
    />
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
